import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Form, FormInstance, Input, Space } from 'antd'
import { UploadFile } from 'antd/lib'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useStocksServiceAcceptInquiryEndpoint,
  useStocksServiceCreateIssuanceEndpoint,
  useStocksServiceGetInquiryEndpointKey,
  useStocksServiceRejectInquiryEndpoint
} from 'src/api/queries'
import {
  ApiError,
  CreateInventoryIssuanceItemDto,
  InquiryDocumentResponse,
  InventoryDocumentState,
  InventoryInquiryItemDto
} from 'src/api/requests'
import {
  CustomItemRender,
  message,
  SaveConfirm,
  Text,
  Upload
} from 'src/components'
import { ConfirmModal } from 'src/components/ConfirmModal'
import { IssuanceHeader } from 'src/features/stock/components'
import {
  FileDocument,
  useFileHandler,
  useScrollToFirstError,
  ValidationError
} from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { AdminConsumableList } from './AdminConsumableList'
import { useStyles } from './styles'

type Props = {
  data?: InquiryDocumentResponse
  form: FormInstance
  isEdit: boolean
  setIsEdit: Dispatch<SetStateAction<boolean>>
}

type InventoryInquiryItemDtoCustom = InventoryInquiryItemDto & {
  form: {
    id: string
    name: string
  }
}

export const StockAdminIssuanceApplicationsWaiting = ({
  data,
  form,
  isEdit,
  setIsEdit
}: Props) => {
  const [isReject, setIsReject] = useState(false)
  const queryClient = useQueryClient()

  const { onScrollToError } = useScrollToFirstError(form)

  const { styles } = useStyles()
  const palette = useTheme()

  const { t } = useLocales()
  const navigate = useNavigate()

  const { id } = useParams()

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Application_${id}`
  })

  const { setExistingFiles, onFileChange, saveFiles, isUploading } =
    useFileHandler({
      form,
      folder: id ? `issuance-${id}` : 'inquiries'
    })

  const onBackClick = () => {
    navigate(paths.stock.applications)
  }

  const { mutate: reject } = useStocksServiceRejectInquiryEndpoint({
    onSuccess: () => {
      message.success(t('stock_applications.rejected_success'))
      navigate(paths.stock.applications)
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const { mutate, isPending } = useStocksServiceCreateIssuanceEndpoint({
    onSuccess: () => {
      message.success(t('stock_applications.successfully_issued'))
      navigate(paths.stock.applications)
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const { mutate: accept, isPending: isAcceptPending } =
    useStocksServiceAcceptInquiryEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useStocksServiceGetInquiryEndpointKey]
        })

        message.success(t('stock_applications.successfully_accepted'))
        setIsEdit(true)
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const onAccept = () => {
    accept({
      id: String(id)
    })
  }

  const onSubmit = async () => {
    try {
      const values = await form.validateFields()

      const updatedFiles = await saveFiles()

      mutate({
        requestBody: {
          baseDocumentId: id,
          contractId: data?.contract?.id,
          documentNumber: String(data?.documentNumber),
          documentDate: dayjs(data?.documentDate).format(yearMonthDaySlash),
          transporterId: values?.transporterId,
          takeoverDate: dayjs(values?.takeoverDate).format(yearMonthDaySlash),
          accompanyingNotice: values?.accompanyingNotice,
          updateReasonId: values?.updateReasonId,
          comment: values?.comment,
          mediaItems: updatedFiles,
          items: values?.medicine?.map(
            (item: CreateInventoryIssuanceItemDto) => ({
              inventoryItemId: item?.inventoryItemId,
              quantity: item?.quantity
            })
          ),
          inquiryItems: values?.inquiryItems?.map(
            (item: InventoryInquiryItemDtoCustom) => ({
              internationalNameId: item?.internationalNameId,
              productFormId: item?.form?.id,
              quantity: item?.quantity
            })
          )
        }
      })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  const onReject = () => {
    reject({
      id: String(id),
      requestBody: {
        comment: form.getFieldValue('commentReject')
      }
    })
    setIsReject(false)
  }

  useEffect(() => {
    if (data) {
      const existingFileIds = data?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      setExistingFiles(existingFileIds)

      const initialFormValues = {
        documentDate: data?.documentDate
          ? dayjs(data?.documentDate)
          : undefined,
        documentNumber: data?.documentNumber,
        supplier: {
          name: data?.supplier?.name,
          id: data?.supplier?.id
        },
        beneficiary: {
          name: data?.beneficiary?.name,
          id: data?.beneficiary?.id
        },
        docs: existingFileIds.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        })),
        inquiryItems: data?.items?.map((item) => ({
          name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
          internationalNameId: item?.internationalName?.id,
          form: {
            name: item?.productForm?.name,
            id: item?.productForm?.id
          },
          quantity: item?.quantity,
          items: item?.beneficiaryStocks || []
        }))
      }

      form.setFieldsValue(initialFormValues)
    }
  }, [data])

  return (
    <>
      <Flex vertical gap={24}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>

          <Space size={8}>
            <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
              {t('stock_applications.print')}
            </Button>

            <>
              <SaveConfirm
                title={`${t('stock_administration.reject')}?`}
                placement="leftBottom"
                onConfirm={() => setIsReject(true)}
              >
                <Button danger>{t('stock_administration.reject')}</Button>
              </SaveConfirm>

              <SaveConfirm
                title={`${t(`stock_applications.${data?.state === InventoryDocumentState.WAITING ? 'accept_inquiry' : 'send_inquiry'}`)}?`}
                placement="leftBottom"
                onConfirm={() =>
                  data?.state === InventoryDocumentState.WAITING
                    ? onAccept()
                    : onSubmit()
                }
                okButtonProps={{
                  loading: isUploading || isPending || isAcceptPending
                }}
              >
                <Button type="primary">
                  {t(
                    `stock_applications.${data?.state === InventoryDocumentState.WAITING ? 'accept_inquiry' : 'send_inquiry'}`
                  )}
                </Button>
              </SaveConfirm>
            </>
          </Space>
        </Flex>

        <Flex vertical gap={16} ref={printableRef}>
          <IssuanceHeader data={data} form={form} />

          <Card>
            <Flex vertical gap={16}>
              <AdminConsumableList
                isEdit={isEdit}
                data={data}
                form={form}
                isSubmitted={false}
              />
            </Flex>
          </Card>

          <Card>
            <Flex vertical gap={16}>
              <Card title={t('stock_applications.uploaded_documents')}>
                <Form.Item name="docs" className="full-width">
                  <Upload
                    defaultFileList={
                      data?.mediaFiles?.map((file) => ({
                        ...file,
                        uid: file.id,
                        name: file.name
                      })) as UploadFile[]
                    }
                    onChange={onFileChange}
                    customRequest={() => null}
                    itemRender={(_, file: FileDocument, __, { remove }) => (
                      <CustomItemRender file={file} onRemove={remove} />
                    )}
                  />
                </Form.Item>
              </Card>

              <Card title={t('common.comment')}>
                <Flex vertical gap={16}>
                  {data?.comment && (
                    <Flex vertical gap={8}>
                      <Text size="lg">
                        {t('stock_applications.beneficiary')}
                      </Text>
                      <div className={styles.commentBeneficiary}>
                        {data?.comment}
                      </div>
                      <Text size="lg" color={palette.colorTextTertiary}>
                        {data?.commentBy?.firstName} {data?.commentBy?.lastName}{' '}
                        - {data?.beneficiary?.name}
                      </Text>
                    </Flex>
                  )}
                  <Flex vertical gap={8}>
                    {t('counterparts.provider')}
                    <Form.Item name="comment">
                      <Input.TextArea
                        rows={5}
                        placeholder={t('common.leave_comment')}
                      />
                    </Form.Item>
                  </Flex>
                </Flex>
              </Card>
            </Flex>
          </Card>
        </Flex>
      </Flex>

      <ConfirmModal
        open={isReject}
        title={`${t('stock_administration.reject')}?`}
        onCancel={() => setIsReject(false)}
        onOk={onReject}
        okButtonProps={{ danger: true }}
      >
        <Card>
          <Form.Item name="commentReject">
            <Input.TextArea rows={5} placeholder={t('common.leave_comment')} />
          </Form.Item>
        </Card>
      </ConfirmModal>
    </>
  )
}
